import { z } from "zod";
import { createLogger } from "../logger";

const log = createLogger("core:parse");

/**
 * Raised when data crossing the DTO→domain boundary fails its schema. Distinct from a
 * transport error so callers can tell "server misbehaved / contract drifted" apart from
 * "request failed".
 */
export class DataContractError extends Error {
  readonly issues: z.ZodError;
  readonly context: string | undefined;

  constructor(issues: z.ZodError, context?: string) {
    super(`Data contract violation${context ? ` in ${context}` : ""}`);
    this.name = "DataContractError";
    this.issues = issues;
    this.context = context;
  }
}

/**
 * Single point where a parse failure is observed. Both strict and safe parsing funnel
 * through here so there is exactly one reporting seam.
 *
 * Emits a readable breakdown of the Zod issues (path / expected / received / message) plus
 * the context via log.error — so in dev it hits the console and in prod it reaches the error
 * sink (redacted). This does NOT change parse behavior: parseWith still throws and parseSafe
 * still returns null after this runs.
 */
function reportParseFailure(
  issues: z.ZodError,
  context: string | undefined
): void {
  const breakdown = issues.issues.map((issue) => {
    const entry: {
      path: string;
      code: string;
      message: string;
      expected?: unknown;
      received?: unknown;
    } = {
      path: issue.path.map((segment) => String(segment)).join(".") || "(root)",
      code: issue.code,
      message: issue.message,
    };
    if ("expected" in issue) entry.expected = issue.expected;
    if ("received" in issue) entry.received = issue.received;
    return entry;
  });

  log.error("Data contract violation", {
    context: context ?? "unknown",
    module: "core:parse",
    issueCount: breakdown.length,
    issues: breakdown,
  });
}

/**
 * Strict parse for data we cannot proceed without (DTO→domain at a service boundary).
 * Throws {@link DataContractError} on failure.
 */
export function parseWith<S extends z.ZodType>(
  schema: S,
  data: unknown,
  context?: string
): z.infer<S> {
  const result = schema.safeParse(data);
  if (!result.success) {
    reportParseFailure(result.error, context);
    throw new DataContractError(result.error, context);
  }
  return result.data;
}

/**
 * Best-effort parse for non-critical data: returns null instead of throwing, so a partial
 * failure degrades the screen gracefully rather than taking it down.
 */
export function parseSafe<S extends z.ZodType>(
  schema: S,
  data: unknown,
  context?: string
): z.infer<S> | null {
  const result = schema.safeParse(data);
  if (!result.success) {
    reportParseFailure(result.error, context);
    return null;
  }
  return result.data;
}
